'use client';

import type { RefObject } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/src/shared/ui/alert-dialog';
import { buttonVariants } from '@/src/shared/ui/button';

interface DeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  isPending?: boolean;
  /** Disable the cancel button, for a caller that also blocks dismissal; off by default. */
  cancelDisabled?: boolean;
  onConfirm: () => void;
  cancelLabel: string;
  confirmLabel: string;
  /** Draws the confirm button in the destructive style; off by default for existing callers. */
  destructive?: boolean;
  /**
   * Where focus goes when the dialog closes. A dialog opened without a trigger
   * otherwise drops focus to the page body.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

export function DeleteConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  isPending,
  cancelDisabled = false,
  onConfirm,
  cancelLabel,
  confirmLabel,
  destructive = false,
  returnFocusRef,
}: DeleteConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        onCloseAutoFocus={(event) => {
          if (!returnFocusRef?.current) return;
          event.preventDefault();
          returnFocusRef.current.focus();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={cancelDisabled}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            className={destructive ? buttonVariants({ variant: 'destructive' }) : undefined}
            disabled={isPending}
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
          >
            {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
